import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { Form, Formik } from 'formik'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'
import { PRIVACY_POLICY_FLATPAGE_URL } from '../../urls'
import FormikCheckbox from '../Formik/FormikCheckbox'
import FormikErrors from '../Formik/FormikErrors'
import FormikRecaptcha from '../Formik/FormikRecaptcha'
import FormikTextField from '../Formik/FormikTextField'
import { Trans, useTranslation } from 'react-i18next'

interface IContactFormData {
  name: string
  email: string
  subject: string
  text: string
  captcha: string
  policyAgreement: boolean
}

const ContactFormFields: React.FC<{
  handleClickPrivacyPolicy?(): void
}> = ({ handleClickPrivacyPolicy }) => {
  const { t } = useTranslation()
  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <FormikTextField name='name' label={t('formLabels.name', 'Name')} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormikTextField name='email' type='email' label={t('formLabels.email', 'Email')} />
      </Grid>
      <Grid item xs={12}>
        <FormikTextField name='subject' label={t('formLabels.subject', 'Subject')} />
      </Grid>
      <Grid item xs={12}>
        <FormikTextField name='text' label={t('formLabels.message', 'Message')} rows={6} multiline />
      </Grid>
      <Grid item xs={12}>
        <FormikRecaptcha name='captcha' />
      </Grid>
      <Grid item xs={12}>
        <FormikCheckbox
          name='policyAgreement'
          formControlLabelProps={{
            label: (
              <span className='pt-5'>
                <Trans i18nKey='formLabels.policyAgreement'>
                  I have read and agree to the
                  <Link to={PRIVACY_POLICY_FLATPAGE_URL} onClick={handleClickPrivacyPolicy}>
                    <strong>Privacy policy</strong>
                  </Link>
                </Trans>
              </span>
            ),
            control: null,
          }}
        />
      </Grid>
    </Grid>
  )
}

interface IProps {
  onSubmit(values: IContactFormData): Promise<void>

  formErrors: { messages: string[]; field?: string }[] | null
  loading?: boolean
}

interface IDialogProps extends IProps {
  variant: 'dialog'

  handleCloseDialog(): void
}

interface IDefaultProps extends IProps {
  variant?: 'default'
}

const ContactForm: React.FC<IDefaultProps | IDialogProps> = ({ onSubmit, formErrors, loading, ...props }) => {
  const { t, i18n } = useTranslation()
  const schema = useMemo(
    () =>
      Yup.object().shape<IContactFormData>({
        name: Yup.string()
          .min(2, t('formErrors.tooShort', 'Too Short!'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
        email: Yup.string()
          .email(t('formErrors.invalidEmail', 'Invalid email'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
        subject: Yup.string()
          .min(5, t('formErrors.tooShort', 'Too Short!'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
        text: Yup.string()
          .min(5, t('formErrors.tooShort', 'Too Short!'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
        captcha: Yup.string().required(t('formErrors.valueIsRequired', 'This value is required')),
        policyAgreement: Yup.boolean().oneOf([true], t('formErrors.fieldMustBeChecked', 'Field must be checked')),
      }),
    [t, i18n.language]
  )
  return (
    <Formik<IContactFormData>
      enableReinitialize
      initialValues={{
        name: '',
        email: '',
        subject: '',
        text: '',
        captcha: '',
        policyAgreement: false,
      }}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {form => (
        <>
          <FormikErrors errors={formErrors} />
          {props.variant === 'dialog' && (
            <>
              <Form>
                <ContactFormFields handleClickPrivacyPolicy={props.handleCloseDialog} />
              </Form>
              <div className='flex justify-end items-center mt-10'>
                <Button type='button' variant='outlined' onClick={props.handleCloseDialog} sx={{ mr: '5px' }}>
                  {t('button.cancel', 'Cancel')}
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  color='secondary'
                  onClick={() => form.handleSubmit()}
                  disabled={loading}
                >
                  {t('button.send', 'Send')}
                </Button>
              </div>
            </>
          )}
          {(!props.variant || props.variant === 'default') && (
            <Form>
              <legend className='legend'>
                {t('yourContactInformation', 'Your contact information')}{' '}
                <span className='legend-label'>{t('required', 'Required')}</span>
              </legend>
              <ContactFormFields />
              <div className='mt-10'>
                <Button type='submit' variant='contained' color='secondary' size='large' disabled={loading}>
                  <span className='font-semibold tracking-widest'>{t('button.send', 'Send')}</span>
                </Button>
              </div>
            </Form>
          )}
        </>
      )}
    </Formik>
  )
}

export default ContactForm
